import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
    Heart, Users, HandHelping, ArrowRight, GraduationCap, Laptop, HeartPulse,
    Sprout, Baby, Home as HomeIcon, MapPin, Award,
} from "lucide-react";
import { api } from "@/lib/api";

const HERO_IMAGES = [
    "https://customer-assets-39nsmqrw.emergentagent.net/job_kabir-ngo-platform/artifacts/f0bznawm_cc2.jpeg",
    "https://customer-assets-39nsmqrw.emergentagent.net/job_kabir-ngo-platform/artifacts/p9t6pjmd_KF%201%20%204%20september%202023.jpg",
];

const PROG_ICONS = {
    "education-support": GraduationCap,
    "digital-literacy": Laptop,
    "nutrition-wellbeing": Baby,
    "women-empowerment": HandHelping,
    "health-awareness": HeartPulse,
    "community-development": Sprout,
};

const IMPACT_LABELS = {
    children_supported: "Children Supported",
    women_empowered: "Women Empowered",
    villages_covered: "Villages Covered",
    digital_literacy_students: "Digital Literacy Students",
    community_programmes: "Community Programmes",
    volunteers_engaged: "Volunteers Engaged",
    partner_organisations: "Partner Organisations",
    years_of_service: "Years of Service",
};

function fmt(n) {
    if (!n && n !== 0) return "—";
    if (n >= 1000) return `${(n / 1000).toFixed(n % 1000 === 0 ? 0 : 1).replace(/\.0$/, "")}K+`;
    return `${n}+`;
}

export default function Home() {
    const [content, setContent] = useState({});
    const [programmes, setProgrammes] = useState([]);
    const [impact, setImpact] = useState({});
    const [blog, setBlog] = useState([]);
    const [gallery, setGallery] = useState([]);
    const [slide, setSlide] = useState(0);

    useEffect(() => {
        const id = setInterval(() => setSlide((s) => (s + 1) % HERO_IMAGES.length), 5500);
        return () => clearInterval(id);
    }, []);

    useEffect(() => {
        Promise.all([
            api.get("/site-content"),
            api.get("/programmes"),
            api.get("/impact-stats"),
            api.get("/blog"),
            api.get("/gallery"),
        ]).then(([c, p, i, b, g]) => {
            setContent(c.data || {});
            setProgrammes(p.data || []);
            setImpact(i.data || {});
            setBlog((b.data || []).slice(0, 3));
            setGallery((g.data || []).slice(0, 6));
        });
    }, []);

    const featured = programmes.find((p) => p.slug === content.featured_programme_slug) || programmes[0];

    return (
        <div data-testid="home-page">
            {/* HERO */}
            <section className="relative min-h-[92vh] flex items-center overflow-hidden" data-testid="hero-section">
                <div className="absolute inset-0">
                    {HERO_IMAGES.map((src, i) => (
                        <img
                            key={src}
                            src={src}
                            alt=""
                            className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-[1600ms] ease-in-out ${
                                i === slide ? "opacity-100" : "opacity-0"
                            }`}
                        />
                    ))}
                    <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/25" />
                </div>
                {/* Slide indicators */}
                <div className="absolute bottom-8 left-1/2 -translate-x-1/2 z-20 flex gap-2">
                    {HERO_IMAGES.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setSlide(i)}
                            data-testid={`hero-slide-${i}`}
                            aria-label={`Slide ${i + 1}`}
                            className={`h-1 rounded-full transition-all duration-300 ${
                                i === slide ? "w-10 bg-white" : "w-5 bg-white/40 hover:bg-white/70"
                            }`}
                        />
                    ))}
                </div>
                <div className="container-hero relative z-10 py-24 md:py-32">
                    <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
                        <div className="label-eyebrow text-brand-ochre mb-6" data-testid="hero-kicker">
                            {content.hero_kicker}
                        </div>
                        <h1 className="font-display text-5xl sm:text-6xl md:text-7xl font-semibold text-white leading-[1.05] tracking-tight text-shadow-strong mb-8" data-testid="hero-title">
                            {content.hero_title}
                        </h1>
                        <p className="text-lg sm:text-xl text-slate-100 leading-relaxed max-w-2xl mb-10">
                            {content.hero_subtitle}
                        </p>
                        <div className="flex flex-wrap gap-3">
                            <Link to="/donate" data-testid="hero-donate-btn" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-brand-forest text-white font-semibold hover:bg-brand-forestHover hover:-translate-y-0.5 transition-transform duration-200 shadow-lg">
                                <Heart size={18} /> {content.cta_donate || "Donate Now"}
                            </Link>
                            <Link to="/contact" data-testid="hero-volunteer-btn" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full bg-white/95 hover:bg-white text-brand-ink font-semibold hover:-translate-y-0.5 transition-transform duration-200">
                                <Users size={18} /> {content.cta_volunteer || "Volunteer With Us"}
                            </Link>
                            <Link to="/csr" data-testid="hero-partner-btn" className="inline-flex items-center gap-2 px-7 py-3.5 rounded-full border border-white/70 text-white font-semibold hover:bg-white/10 transition-colors duration-200">
                                <HandHelping size={18} /> {content.cta_partner || "Partner With Us"}
                            </Link>
                        </div>
                    </motion.div>
                </div>
            </section>

            {/* WHO WE ARE */}
            <section className="py-24 md:py-32" data-testid="who-we-are">
                <div className="container-hero grid grid-cols-1 md:grid-cols-12 gap-12 items-start">
                    <div className="md:col-span-5">
                        <div className="label-eyebrow text-brand-forest mb-4">Who We Are</div>
                        <h2 className="font-display text-4xl sm:text-5xl font-medium text-brand-ink leading-tight">
                            {content.who_we_are_title}
                        </h2>
                    </div>
                    <div className="md:col-span-6 md:col-start-7">
                        <p className="text-lg text-slate-700 leading-relaxed">{content.who_we_are_body}</p>
                        <div className="mt-8 flex items-center gap-6 text-sm text-slate-500">
                            <div className="flex items-center gap-2"><Award size={18} className="text-brand-forest" strokeWidth={1.5} /> 12A · 80G · CSR-1</div>
                            <div className="flex items-center gap-2"><MapPin size={18} className="text-brand-forest" strokeWidth={1.5} /> Bundelkhand, MP</div>
                        </div>
                    </div>
                </div>
            </section>

            {/* WHAT WE DO */}
            <section className="py-24 md:py-32 bg-brand-stone" data-testid="what-we-do">
                <div className="container-hero">
                    <div className="max-w-2xl mb-14">
                        <div className="label-eyebrow text-brand-forest mb-4">What We Do</div>
                        <h2 className="font-display text-4xl sm:text-5xl font-medium text-brand-ink leading-tight">
                            {content.what_we_do_title}
                        </h2>
                        <p className="text-lg text-slate-600 mt-4">{content.what_we_do_subtitle}</p>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                        {programmes.map((p, i) => {
                            const Icon = PROG_ICONS[p.slug] || Sprout;
                            return (
                                <motion.div
                                    key={p.id}
                                    initial={{ opacity: 0, y: 20 }}
                                    whileInView={{ opacity: 1, y: 0 }}
                                    viewport={{ once: true }}
                                    transition={{ duration: 0.4, delay: i * 0.08 }}
                                >
                                    <Link
                                        to={`/programmes/${p.slug}`}
                                        data-testid={`programme-card-${p.slug}`}
                                        className="group flex flex-col bg-white rounded-2xl overflow-hidden border border-black/5 hover:border-brand-forest/30 hover:shadow-lg hover:-translate-y-1 transition-transform duration-300 h-full"
                                    >
                                        {p.cover_image && (
                                            <div className="relative h-48 overflow-hidden bg-brand-stone">
                                                <img
                                                    src={p.cover_image}
                                                    alt={p.title}
                                                    loading="lazy"
                                                    className="w-full h-full object-cover group-hover:scale-[1.05] transition-transform duration-500"
                                                />
                                                <div className="absolute top-3 left-3 w-10 h-10 rounded-lg bg-white/95 text-brand-forest grid place-items-center shadow-sm">
                                                    <Icon size={18} strokeWidth={1.75} />
                                                </div>
                                            </div>
                                        )}
                                        <div className="flex flex-col flex-1 p-6">
                                            <h3 className="font-display text-2xl font-medium text-brand-ink leading-snug">
                                                {p.title}
                                            </h3>
                                            <p className="text-slate-600 text-sm leading-relaxed mt-3 flex-1">
                                                {p.summary}
                                            </p>
                                            {p.outcomes?.length > 0 && (
                                                <ul className="flex flex-wrap gap-1.5 mt-4 pt-4 border-t border-black/5">
                                                    {p.outcomes.slice(0, 3).map((o) => (
                                                        <li
                                                            key={o}
                                                            className="text-[11px] px-2.5 py-1 rounded-full bg-brand-stone text-slate-700"
                                                        >
                                                            {o}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </Link>
                                </motion.div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* IMPACT */}
            <section className="py-24 md:py-32" data-testid="impact-section">
                <div className="container-hero">
                    <div className="max-w-2xl mb-14">
                        <div className="label-eyebrow text-brand-forest mb-4">Our Impact</div>
                        <h2 className="font-display text-4xl sm:text-5xl font-medium text-brand-ink leading-tight">A decade of measurable change.</h2>
                        <p className="text-lg text-slate-600 mt-4">{impact.intro_text}</p>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-4 gap-y-10">
                        {Object.entries(IMPACT_LABELS).map(([k, label]) => (
                            <div key={k} data-testid={`impact-${k}`} className="border-t border-black/10 pt-5">
                                <div className="font-display text-4xl sm:text-5xl font-semibold text-brand-forest">{fmt(impact[k])}</div>
                                <div className="text-xs uppercase tracking-[0.15em] text-slate-500 mt-2 font-semibold">{label}</div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* FEATURED PROGRAMME */}
            {featured && (
                <section className="py-24 md:py-32 bg-brand-ink text-white" data-testid="featured-programme">
                    <div className="container-hero grid grid-cols-1 md:grid-cols-12 gap-10 items-center">
                        <div className="md:col-span-6">
                            <img src={featured.cover_image} alt={featured.title} className="w-full aspect-[4/3] object-cover rounded-2xl" />
                        </div>
                        <div className="md:col-span-6">
                            <div className="label-eyebrow text-brand-ochre mb-4">Featured Programme</div>
                            <h2 className="font-display text-4xl sm:text-5xl font-medium leading-tight">{featured.title}</h2>
                            <p className="text-slate-300 mt-5 text-lg leading-relaxed">{featured.description || featured.summary}</p>
                            <Link to={`/programmes/${featured.slug}`} className="mt-8 inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-brand-ink font-semibold hover:bg-brand-ochre hover:text-white hover:-translate-y-0.5 transition-transform duration-200">
                                Explore programme <ArrowRight size={16} />
                            </Link>
                        </div>
                    </div>
                </section>
            )}

            {/* LATEST MEDIA */}
            <section className="py-24 md:py-32" data-testid="latest-media">
                <div className="container-hero">
                    <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                        <div>
                            <div className="label-eyebrow text-brand-forest mb-3">Latest Stories</div>
                            <h2 className="font-display text-4xl sm:text-5xl font-medium">From the field</h2>
                        </div>
                        <Link to="/media" className="text-sm font-semibold text-brand-forest inline-flex items-center gap-1 hover:gap-2 transition-[gap] duration-200">View all <ArrowRight size={14} /></Link>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        {blog.map((b) => (
                            <Link key={b.id} to={`/media/${b.slug}`} data-testid={`blog-preview-${b.slug}`} className="group bg-white rounded-2xl overflow-hidden border border-black/5 hover:shadow-lg hover:-translate-y-1 transition-transform duration-300">
                                <div className="aspect-[16/10] overflow-hidden bg-brand-stone">
                                    <img src={b.cover_image} alt={b.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                                </div>
                                <div className="p-6">
                                    <div className="text-[11px] uppercase tracking-[0.15em] text-brand-forest font-semibold">{b.category}</div>
                                    <h3 className="font-display text-2xl mt-2 leading-snug">{b.title}</h3>
                                    <p className="text-slate-600 text-sm mt-2 line-clamp-2">{b.excerpt}</p>
                                </div>
                            </Link>
                        ))}
                    </div>
                </div>
            </section>

            {/* LATEST GALLERY */}
            <section className="py-24 md:py-32 bg-brand-stone" data-testid="latest-gallery">
                <div className="container-hero">
                    <div className="flex flex-wrap items-end justify-between gap-4 mb-10">
                        <div>
                            <div className="label-eyebrow text-brand-forest mb-3">Gallery</div>
                            <h2 className="font-display text-4xl sm:text-5xl font-medium">Moments from our work</h2>
                        </div>
                        <Link to="/gallery" className="text-sm font-semibold text-brand-forest inline-flex items-center gap-1 hover:gap-2 transition-[gap] duration-200">View all <ArrowRight size={14} /></Link>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                        {gallery.map((g, i) => (
                            <div key={g.id} data-testid={`gallery-preview-${i}`} className={`overflow-hidden rounded-xl bg-white ${i % 5 === 0 ? "aspect-[4/5]" : "aspect-square"}`}>
                                <img src={g.image_url} alt={g.caption} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* GET INVOLVED */}
            <section className="py-24 md:py-32" data-testid="get-involved">
                <div className="container-hero">
                    <div className="rounded-3xl bg-brand-forest text-white p-10 md:p-16 grid md:grid-cols-2 gap-10 items-center">
                        <div>
                            <div className="label-eyebrow text-brand-ochre mb-4">Get Involved</div>
                            <h2 className="font-display text-4xl sm:text-5xl font-medium leading-tight">Every hour, every rupee, every partnership counts.</h2>
                            <p className="text-white/80 mt-4 max-w-lg">Join us as a donor, volunteer or CSR partner and help us go deeper in Bundelkhand.</p>
                        </div>
                        <div className="grid sm:grid-cols-3 gap-3">
                            <Link to="/donate" className="px-5 py-4 rounded-xl bg-white/10 hover:bg-white/20 text-center transition-colors duration-200">
                                <Heart className="mx-auto mb-2" strokeWidth={1.5} />
                                <div className="font-semibold text-sm">Donate</div>
                            </Link>
                            <Link to="/contact" className="px-5 py-4 rounded-xl bg-white/10 hover:bg-white/20 text-center transition-colors duration-200">
                                <Users className="mx-auto mb-2" strokeWidth={1.5} />
                                <div className="font-semibold text-sm">Volunteer</div>
                            </Link>
                            <Link to="/csr" className="px-5 py-4 rounded-xl bg-white/10 hover:bg-white/20 text-center transition-colors duration-200">
                                <HandHelping className="mx-auto mb-2" strokeWidth={1.5} />
                                <div className="font-semibold text-sm">Partner</div>
                            </Link>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
